/*
 * @file: ubcsend
 * @author: xuyong08
 */
import {UbcLogger} from '@baidu/ubc-report-sdk';
import {UbcSendOptions, UbcInitOptions} from '@typing/ubc/ubcLog.interface';
import {deduplicationManager, generateExposureKey} from './deduplication';
import { getLocation } from '@utils/util';

export const ubc = new Map<string, UbcLogger>();

/**
 * @description: ubc日志初始化
 * @param {string} eventName logger的关联标识，在ubcinit/ubcSend时传入
 * @param {number} serverId ubcId
 * @param {UbcInitOptions} options ubc配置
 * @return {void}
 */
export const ubcInit = (eventName: string, serverId: number, options: UbcInitOptions) => {
    try {
        if (!ubc.has(eventName)) {
            ubc.set(
                eventName,
                new UbcLogger({
                    // serverId ubcid，ubc平台申请获取的日志id
                    serverId,
                    queryParams: {
                        appname: options.appname || ''
                    },
                    testMode: options.testMode,
                    from: 'fe-doctor-workbench',
                })
            );
        }
    } catch (error) {
        console.error(error);
    }
};

/**
 * 发送日志
 * @param options 配置参数
 * @param eventName logger的关联标识
 */
export const send = (options: UbcSendOptions) => {
    const ext = options.ext || {};
    ubc.get('mzDoctor')?.send(
        {
            page: options.page || window.location.pathname,
            type: options.type,
            duration: options?.duration,
            value: options.value,
            ext: {
                pd: 'fe-doctor-workbench',

                /**
                 * 业务线类型
                 */
                // eslint-disable-next-line camelcase
                apptype: '12133',
                ...ext
            }
        },
        {
            useSendBeacon: true
        }
    );
};

/**
 * 从 product_info 中生成默认的 customKey
 * @param productInfo product_info 对象或JSON字符串
 * @returns 拼接后的字符串
 */
function generateCustomKeyFromProductInfo(productInfo: any): string | undefined {
    if (!productInfo) return undefined;
    
    try {
        const info = typeof productInfo === 'string' ? JSON.parse(productInfo) : productInfo;
        
        // 提取所有值并拼接
        const values = Object.values(info)
            .filter(v => v !== undefined && v !== null && v !== '')
            .map(v => String(v))
            .sort(); // 排序确保一致性
        return values.length > 0 ? values.join('_') : undefined;
    } catch (error) {
        console.error('Failed to parse product_info for customKey:', error);
        return undefined;
    }
}

/**
 * 带去重检查的埋点发送函数
 * @param options 埋点参数（包含可选的 customKey）
 * @returns 是否成功发送埋点
 */
export const sendWithDeduplication = (options: UbcSendOptions): boolean => {
    // 确保是展现埋点类型
    if (options.type !== 'view') {
        send(options);
        return true;
    }

    // 如果没有提供 customKey，尝试从 ext.product_info 自动生成
    let customKey = options.customKey;
    if (!customKey) {
        customKey = generateCustomKeyFromProductInfo(options.ext?.product_info);
    }

    // 生成唯一键
    const exposureKey = generateExposureKey({
        type: options.type,
        value: options.value,
        customKey
    });
    // 检查是否已上报
    if (deduplicationManager.hasReported(exposureKey)) {
        if (process.env.NODE_ENV !== 'production') {
            console.log(`埋点已上报，跳过: ${exposureKey}`);
        }
        return false;
    }
    
    // 标记为已上报并发送埋点
    deduplicationManager.markAsReported(exposureKey);
    send(options);
    return true;
};